import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import PageHeader from '../components/PageHeader';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FeedbackReportGrid from '../components/FeedbackReportGrid';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useAssignmentService } from '../services/assignmentService';
import { useFeedbackService, FeedbackType } from '../services/feedbackService';




const FeedbackReportPage: React.FC = () => {
  const navigate = useNavigate();
  const assignmentService = useAssignmentService();
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const [searchParams] = useSearchParams();
  const transcript_id = Number(searchParams.get('transcript_id'));
  const { fetchSingleFeedback } = useFeedbackService();
  const [feedback, setFeedback] = useState<FeedbackType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFeedback = async () => {
      try {
        setLoading(true);
        const feedbackData = await fetchSingleFeedback(feedbackId!);
        setFeedback(feedbackData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    };
    if (feedbackId) loadFeedback();
  }, [feedbackId]);

  function handleBack(): void {
    navigate(-1);
  }

  const handleDownloadTranscript = async (transcriptId: number) => {
    try {
      const response = await assignmentService.downloadTranscriptResponse(transcriptId);
      const disposition = response.headers.get("Content-Disposition");

      // Fail if no content-disposition or filename
      if (!disposition) {
        alert("No Content-Disposition header found. Cannot download transcript.");
        return;
      }

      // Regex for filename (handles quotes and non-quotes, as well as UTF-8 filenames)
      let filename: string | null = null;
      const filenameRegex = /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i;
      const match = disposition.match(filenameRegex);
      if (match && match[1]) {
        filename = decodeURIComponent(match[1]);
      }

      if (!filename) {
        alert("Could not determine transcript filename from server response.");
        return;
      }

      const blob = await response.blob();
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Transcript download failed " + transcriptId);
    }
  };

  if (!feedbackId) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography>Feedback ID not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, overflow: 'auto' }}>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <PageHeader
          title={feedback! ? feedback.roleplay_title! : ''}
          subtitle="Detailed feedback analysis from your roleplay session"
          onBack={handleBack}
          showBackButton={true}
        />
        {!isNaN(transcript_id) && transcript_id > 0 && (
          <Button
            startIcon={<AssignmentIcon />}
            onClick={() => handleDownloadTranscript(transcript_id)}
            size="small"
            color="primary"
            sx={{ m: 0.5 }}
          >
            Download Transcript
          </Button>
        )}
      </Container>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="h6">Loading feedback...</Typography>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Feedback Grid */}
      {!loading && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'auto', paddingBottom: '2rem' }}>
          <FeedbackReportGrid feedback={feedback} />
        </Box>
      )}
    </Container>
  );
};

export default FeedbackReportPage;